// The dashboard briefing (ZENITH.md §16; ADR-0210, ADR-0407): a few sentences
// from a language model over the figures the widgets on this page show, asked
// for by a person and never on a cadence. It is its own request, so a briefing
// that fails costs the widgets nothing (ADR-0210 decision 4), and every refusal
// lands here, in the reason's own words. A deployment that names no provider
// is not offered the act at all (#2096): /me's `ai_assistant` is false there,
// so the Overview draws no *Brief me*, and `no_model_provider` reaches this
// card only from a deployment that stopped naming one mid-session.
import { api } from "../api/client";
import { useMutation } from "../api/mutation";
import type { Problem } from "../api/problem";
import type { components } from "../api/schema";
import { ProblemBox, When } from "../ui";
import { paragraphs } from "./format";
import { CloseIcon, Glyph } from "../icons";

type Briefing = components["schemas"]["Briefing"];
type WidgetScope = components["schemas"]["WidgetScope"];
type WidgetRef = components["schemas"]["DashboardWidgetRef"];

/** Asks for a briefing over `widgets` at `scope`. The body is the dashboard
 * read's own, so the server computes the same figures this page shows. */
export function useBriefing() {
  const ask = useMutation();
  const run = (scope: WidgetScope, widgets: WidgetRef[]) =>
    ask.runWith(() => api.POST("/ai:briefing", { params: { query: scope }, body: { widgets } }));
  return { run, busy: ask.busy, problem: ask.problem, clear: ask.clear };
}

export function BriefingCard({ briefing, busy, problem, onDismiss }: {
  briefing?: Briefing; busy: boolean; problem?: Problem; onDismiss(): void;
}) {
  if (!briefing && !busy && !problem) return null;
  return (
    <section className="panel" aria-label="Briefing" aria-busy={busy}>
      <div className="edit-bar">
        <h2 style={{ margin: 0 }}>Briefing</h2>
        <span style={{ flex: 1 }} />
        {!busy && <button type="button" className="btn btn-quiet" onClick={onDismiss} aria-label="Close the briefing"><Glyph icon={CloseIcon} /></button>}
      </div>
      {busy && <p className="muted" role="status">Asking for a briefing on the figures on this page…</p>}
      {!busy && problem && <ProblemBox problem={problem} />}
      {!busy && !problem && briefing && (
        <>
          {/* Restates ADR-0210 decision 4 — a model that declines is told as that,
              and nothing is invented in its place. */}
          {briefing.outcome === "declined"
            ? <p>The assistant declined to give a briefing on these figures.</p>
            : paragraphs(briefing.text ?? "").map((p, i) => <p key={i}>{p}</p>)}
          {/* Restates ADR-0210 decisions 2 and 6a — the briefing is drawn from the
              figures alone, and an answer from memory sent nothing. */}
          <p className="dim small">
            Written by an AI model from the figures computed at <When value={briefing.figures_generated_at} />. Check the widgets before acting on it.
            {briefing.cached && <> The same figures were briefed a moment ago, so this is that answer and nothing new was sent.</>}
          </p>
        </>
      )}
    </section>
  );
}
